import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { CloseButton } from './CloseButton';

// ============ TYPES ============

interface SignatureMoveDefinition {
  id: string;
  baseName: string;
  description: string;
  category: 'offensive' | 'defensive' | 'utility' | 'support';
  element: 'flame' | 'storm' | 'tide' | 'shadow' | 'iron' | 'spirit';
  baseDamage: number;
  damageScaling: number;
  powerCost: number;
  cooldownMs: number;
  range: number;
  aoeRadius: number;
  evolutionThreshold: number;
  evolvedForm: string | null;
  rarity: 'rare' | 'epic' | 'legendary';
  color: string;
}

interface PlayerSignatureMove {
  definitionId: string;
  customAlias: string;
  useCount: number;
  killCount: number;
  acquired: number;
  isEquipped: boolean;
  evolved: boolean;
  definition?: SignatureMoveDefinition;
}

interface WildcardPowerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  socket: Socket | null;
}

// ============ CONSTANTS ============

const ELEMENT_COLORS: Record<string, string> = {
  flame: '#ff6b35',
  storm: '#00bfff',
  tide: '#1e90ff',
  shadow: '#4b0082',
  iron: '#708090',
  spirit: '#9932cc',
};

const ELEMENT_NAMES: Record<string, string> = {
  flame: 'Flame',
  storm: 'Storm',
  tide: 'Tide',
  shadow: 'Shadow',
  iron: 'Iron',
  spirit: 'Spirit',
};

const CATEGORY_NAMES: Record<string, string> = {
  offensive: 'Offensive',
  defensive: 'Defensive',
  utility: 'Utility',
  support: 'Support',
};

const RARITY_COLORS: Record<string, string> = {
  rare: '#3b82f6',
  epic: '#a855f7',
  legendary: '#f59e0b',
};

// ============ STYLES ============

const injectStyles = (): void => {
  const styleId = 'signature-move-panel-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .signature-panel-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    
    .signature-panel {
      background: #0a0a1a;
      border: 2px solid #1a1a2e;
      width: 500px;
      max-height: 80vh;
      overflow-y: auto;
      font-family: 'Sometype Mono', monospace;
    }
    
    .signature-panel-header {
      background: #1a1a2e;
      padding: 12px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #2a2a4e;
    }
    
    .signature-panel-title {
      color: #ffffff;
      font-size: 14px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    
    .signature-panel-content {
      padding: 16px;
    }
    
    .signature-empty {
      color: #666;
      text-align: center;
      padding: 40px 20px;
      font-size: 12px;
    }
    
    .signature-move-card {
      background: #111122;
      border: 1px solid #2a2a4e;
      margin-bottom: 12px;
      padding: 12px;
    }
    
    .signature-move-card.equipped {
      border-color: #4fc3f7;
      box-shadow: 0 0 8px rgba(79, 195, 247, 0.3);
    }
    
    .signature-move-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;
    }
    
    .signature-move-name {
      font-size: 14px;
      font-weight: bold;
      color: #ffffff;
    }
    
    .signature-move-base-name {
      font-size: 10px;
      color: #888;
      margin-top: 2px;
    }
    
    .signature-move-badges {
      display: flex;
      gap: 6px;
    }
    
    .signature-badge {
      font-size: 9px;
      padding: 2px 6px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .signature-move-stats {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin: 12px 0;
      font-size: 10px;
    }
    
    .signature-stat {
      background: #0a0a1a;
      padding: 6px 8px;
      border: 1px solid #1a1a2e;
    }
    
    .signature-stat-label {
      color: #666;
      margin-bottom: 2px;
    }
    
    .signature-stat-value {
      color: #fff;
      font-weight: bold;
    }
    
    .signature-move-progress {
      margin: 12px 0;
    }
    
    .signature-progress-label {
      font-size: 10px;
      color: #888;
      margin-bottom: 4px;
      display: flex;
      justify-content: space-between;
    }
    
    .signature-progress-bar {
      height: 4px;
      background: #1a1a2e;
    }
    
    .signature-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #4fc3f7, #29b6f6);
      transition: width 0.3s ease;
    }
    
    .signature-move-actions {
      display: flex;
      gap: 8px;
      margin-top: 12px;
    }
    
    .signature-action-btn {
      flex: 1;
      padding: 8px 12px;
      font-size: 10px;
      font-family: 'Sometype Mono', monospace;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      cursor: pointer;
      border: 1px solid #2a2a4e;
      background: #1a1a2e;
      color: #888;
    }
    
    .signature-action-btn:hover {
      background: #2a2a4e;
      color: #fff;
    }
    
    .signature-action-btn.primary {
      background: #4fc3f7;
      border-color: #4fc3f7;
      color: #000;
    }
    
    .signature-action-btn.primary:hover {
      background: #29b6f6;
    }
    
    .signature-action-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .signature-rename-input {
      flex: 1;
      padding: 8px 12px;
      font-size: 11px;
      font-family: 'Sometype Mono', monospace;
      background: #0a0a1a;
      border: 1px solid #2a2a4e;
      color: #fff;
    }
    
    .signature-rename-input:focus {
      outline: none;
      border-color: #4fc3f7;
    }
    
    .signature-description {
      font-family: var(--font-lookout);
      font-size: 7px;
      color: #888;
      line-height: 1.4;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #1a1a2e;
    }
    
    .signature-equipped-badge {
      background: #4fc3f7;
      color: #000;
      font-size: 9px;
      padding: 2px 6px;
      text-transform: uppercase;
      font-weight: bold;
    }
    
    .signature-evolved-badge {
      background: #ffd700;
      color: #000;
    }
  `;
  document.head.appendChild(style);
};

// ============ COMPONENT ============

export const WildcardPowerPanel: React.FC<WildcardPowerPanelProps> = ({
  isOpen,
  onClose,
  socket,
}) => {
  const [moves, setMoves] = useState<PlayerSignatureMove[]>([]);
  const [equippedId, setEquippedId] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  const cooldownRemainingMs: number = cooldownUntil ? Math.max(0, cooldownUntil - Date.now()) : 0;
  const cooldownRemainingSeconds: number = Math.ceil(cooldownRemainingMs / 1000);
  const isOnCooldown: boolean = cooldownRemainingMs > 0;

  // Inject styles on mount
  useEffect(() => {
    injectStyles();
  }, []);

  // Fetch moves when panel opens
  useEffect(() => {
    if (isOpen && socket) {
      setLoading(true);
      socket.emit('wildcard:list');
    }
  }, [isOpen, socket]);

  // Listen for signature events
  useEffect(() => {
    if (!socket) return;

    const handleList = (data: { moves: PlayerSignatureMove[]; equipped: string | null; cooldownUntil: number | null }): void => {
      setMoves(data.moves);
      setEquippedId(data.equipped);
      setCooldownUntil(data.cooldownUntil || null);
      setLoading(false);
    };

    const handleEquipped = (data: { definitionId: string }): void => {
      setEquippedId(data.definitionId);
      setMoves(prev => prev.map(m => ({
        ...m,
        isEquipped: m.definitionId === data.definitionId,
      })));
    };

    const handleRenamed = (data: { definitionId: string; newAlias: string }): void => {
      setMoves(prev => prev.map(m => 
        m.definitionId === data.definitionId 
          ? { ...m, customAlias: data.newAlias }
          : m
      ));
      setRenamingId(null);
      setRenameValue('');
    };

    const handleUnlocked = (data: { move: PlayerSignatureMove; definition: SignatureMoveDefinition }): void => {
      setMoves(prev => [...prev, { ...data.move, definition: data.definition }]);
    };

    const handleEvolved = (data: { oldMove: PlayerSignatureMove; newMove: PlayerSignatureMove; newDefinition: SignatureMoveDefinition }): void => {
      setMoves(prev => prev.map(m => 
        m.definitionId === data.oldMove.definitionId
          ? { ...data.newMove, definition: data.newDefinition }
          : m
      ));
      if (equippedId === data.oldMove.definitionId) {
        setEquippedId(data.newMove.definitionId);
      }
    };

    const handleActivated = (data: { cooldownUntil: number }): void => {
      setCooldownUntil(data.cooldownUntil);
    };

    socket.on('wildcard:list', handleList);
    socket.on('wildcard:equipped', handleEquipped);
    socket.on('wildcard:renamed', handleRenamed);
    socket.on('wildcard:unlocked', handleUnlocked);
    socket.on('wildcard:evolved', handleEvolved);
    socket.on('wildcard:activated', handleActivated);

    return () => {
      socket.off('wildcard:list', handleList);
      socket.off('wildcard:equipped', handleEquipped);
      socket.off('wildcard:renamed', handleRenamed);
      socket.off('wildcard:unlocked', handleUnlocked);
      socket.off('wildcard:evolved', handleEvolved);
      socket.off('wildcard:activated', handleActivated);
    };
  }, [socket, equippedId]);

  const handleEquip = useCallback((definitionId: string) => {
    socket?.emit('wildcard:equip', { definitionId });
  }, [socket]);

  const handleStartRename = useCallback((move: PlayerSignatureMove) => {
    setRenamingId(move.definitionId);
    setRenameValue(move.customAlias);
  }, []);

  const handleConfirmRename = useCallback(() => {
    if (renamingId && renameValue.trim()) {
      socket?.emit('wildcard:rename', { definitionId: renamingId, newAlias: renameValue.trim() });
    }
  }, [socket, renamingId, renameValue]);

  const handleCancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue('');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirmRename();
    } else if (e.key === 'Escape') {
      handleCancelRename();
    }
  }, [handleConfirmRename, handleCancelRename]);

  if (!isOpen) return null;

  return (
    <div className="signature-panel-overlay" onClick={onClose}>
      <div className="signature-panel" onClick={e => e.stopPropagation()}>
        <div className="signature-panel-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="signature-panel-title">Signature Moves</span>
            {isOnCooldown && (
              <span style={{ color: '#94a3b8', fontSize: 10 }}>
                Cooldown: {cooldownRemainingSeconds}s
              </span>
            )}
          </div>
          <CloseButton onClick={onClose} />
        </div>
        
        <div className="signature-panel-content">
          {loading ? (
            <div className="signature-empty">Loading...</div>
          ) : moves.length === 0 ? (
            <div className="signature-empty">
              No signature moves acquired yet.<br /><br />
              Find Essence items in whirlpools to unlock signature moves.
            </div>
          ) : (
            moves.map(move => {
              const def = move.definition;
              if (!def) return null;
              
              const isRenaming = renamingId === move.definitionId;
              const evolutionProgress = def.evolutionThreshold > 0 
                ? Math.min(100, (move.useCount / def.evolutionThreshold) * 100)
                : 100;
              
              return (
                <div 
                  key={move.definitionId} 
                  className={`signature-move-card ${move.isEquipped ? 'equipped' : ''}`}
                >
                  <div className="signature-move-header">
                    <div>
                      <div className="signature-move-name" style={{ color: def.color }}>
                        {move.customAlias}
                      </div>
                      <div className="signature-move-base-name">
                        {def.baseName}
                      </div>
                    </div>
                    <div className="signature-move-badges">
                      {move.isEquipped && (
                        <span className="signature-badge signature-equipped-badge">EQUIPPED</span>
                      )}
                      {move.evolved && (
                        <span className="signature-badge signature-evolved-badge">EVOLVED</span>
                      )}
                      <span
                        className="signature-badge"
                        style={{ background: '#374151', color: '#ffffff' }}
                      >
                        {CATEGORY_NAMES[def.category]}
                      </span>
                      <span 
                        className="signature-badge" 
                        style={{ 
                          background: ELEMENT_COLORS[def.element], 
                          color: def.element === 'iron' ? '#fff' : '#000' 
                        }}
                      >
                        {ELEMENT_NAMES[def.element]}
                      </span>
                      <span 
                        className="signature-badge" 
                        style={{ background: RARITY_COLORS[def.rarity], color: '#fff' }}
                      >
                        {def.rarity.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  
                   <div className="signature-move-stats">
                     <div className="signature-stat">
                       <div className="signature-stat-label">Damage</div>
                       <div className="signature-stat-value">{def.baseDamage}</div>
                     </div>
                     <div className="signature-stat">
                       <div className="signature-stat-label">Power Cost</div>
                       <div className="signature-stat-value" style={{ color: '#4fc3f7' }}>{def.powerCost}%</div>
                     </div>
                     <div className="signature-stat">
                       <div className="signature-stat-label">Cooldown</div>
                       <div className="signature-stat-value">{def.cooldownMs / 1000}s</div>
                     </div>
                     <div className="signature-stat">
                       <div className="signature-stat-label">Range</div>
                       <div className="signature-stat-value">{def.range} tiles</div>
                     </div>
                     <div className="signature-stat">
                       <div className="signature-stat-label">AoE</div>
                       <div className="signature-stat-value">{def.aoeRadius > 0 ? `${def.aoeRadius} tiles` : 'Single'}</div>
                     </div>
                     <div className="signature-stat">
                       <div className="signature-stat-label">Uses</div>
                       <div className="signature-stat-value">{move.useCount}</div>
                     </div>
                     <div className="signature-stat">
                       <div className="signature-stat-label">Kills</div>
                       <div className="signature-stat-value">{move.killCount}</div>
                    </div>
                  </div>
                  
                  {!move.evolved && def.evolutionThreshold > 0 && (
                    <div className="signature-move-progress">
                      <div className="signature-progress-label">
                        <span>Evolution Progress</span>
                        <span>{move.useCount} / {def.evolutionThreshold}</span>
                      </div>
                      <div className="signature-progress-bar">
                        <div 
                          className="signature-progress-fill" 
                          style={{ width: `${evolutionProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="signature-description">
                    {def.description}
                  </div>
                  
                  <div className="signature-move-actions">
                    {isRenaming ? (
                      <>
                        <input
                          type="text"
                          className="signature-rename-input"
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          autoFocus
                          maxLength={30}
                        />
                        <button 
                          className="signature-action-btn primary"
                          onClick={handleConfirmRename}
                        >
                          Save
                        </button>
                        <button 
                          className="signature-action-btn"
                          onClick={handleCancelRename}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          className="signature-action-btn"
                          onClick={() => handleStartRename(move)}
                        >
                          Rename
                        </button>
                        <button 
                          className={`signature-action-btn ${!move.isEquipped ? 'primary' : ''}`}
                          onClick={() => handleEquip(move.definitionId)}
                          disabled={move.isEquipped}
                        >
                          {move.isEquipped ? 'Equipped' : 'Equip'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default WildcardPowerPanel;
